import React from 'react';
import { StyleSheet, View, ScrollView, TouchableOpacity, Text, useWindowDimensions } from 'react-native';
import Button from "../../../components/Button";
import { useNavigation } from "@react-navigation/native";
import ProductViewComponent from "./ProductViewComponent";
import { SmallText } from '../../../config/theme/globalStyles';
import { Octicons } from '@expo/vector-icons';
import { CheckBox } from "react-native-elements";
import { CatalogVitrineFields } from "../../../components/CatalogVitrineFields";
import ProductCatalogFilters from "../../../components/ProductCatalogFilters";
import AdminAlert from "../../../components/AdminAlert";
import { ADMIN_COLORS, ADMIN_FONT } from "../../../config/theme/adminColors";

const GenericProduct = ({
  productData,
  onClose,
  tree,
  setTree,
  expandedCategoryIds,
  setExpandedCategoryIds,
  loadingSave,
  handleEdit,
  onDelete,
  isDeleting,
  vitrineRef,
  vitrineFeedback,
}) => {
  const navigation = useNavigation();
  const { width: screenWidth, height: windowHeight } = useWindowDimensions();

  // Produtos IAP (Apple/Google) são sincronizados das lojas: aqui só se vincula
  // conteúdo (categorias/módulos). Ofertas e exclusão não se aplicam.
  const isIAP = productData?.marketplace === 'IAP';

  const editOffers = () => {
    onClose()
    // Passa o produto adiante para que a tela de ofertas consiga reabrir este
    // mesmo modal ao clicar em "Voltar".
    navigation.navigate('ProductConfig', { productId: productData.id, product: productData })
  }

  // Largura/altura dependem da janela — não podem morar no StyleSheet estático
  // (calculado uma única vez no import), senão o modal trava no tamanho de
  // quando a página carregou e não acompanha resize (Macbook em split) nem
  // rotação de celular.
  const responsiveModalStyle = {
    width: screenWidth > 980 ? screenWidth * 0.6 : screenWidth * 0.9,
    maxHeight: windowHeight * 0.9,
  };

  return (
    <View style={[styles.modalView, responsiveModalStyle]}>
      <View style={styles.modalHeader}>
        <TouchableOpacity onPress={onClose} style={styles.closeX} accessibilityLabel="Fechar">
          <Text style={styles.closeXText}>✕</Text>
        </TouchableOpacity>
      </View>
      <ScrollView
        style={{ width: '100%', flexGrow: 1, flexShrink: 1, minHeight: 0 }}
        contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 20, paddingTop: 4 }}
        showsVerticalScrollIndicator={true}
      >
        <ProductViewComponent productData={productData} />
        <CatalogVitrineFields
          ref={vitrineRef}
          collectionName="products"
          docId={productData?.id}
          name={productData?.name}
          accessIds={
            productData?.productId ? [productData.productId] : productData?.id ? [productData.id] : []
          }
        />
        <ProductCatalogFilters
          collectionName="products"
          docId={productData?.id}
          productName={productData?.name}
          accessIds={
            productData?.productId ? [productData.productId] : productData?.id ? [productData.id] : []
          }
        />
        <View style={{ marginTop: "1rem", backgroundColor: "white" }}>
          <SmallText textAlign="left" color={ADMIN_COLORS.label} fontFamily={ADMIN_FONT}>
            Vincular a Categorias ou Módulos:
          </SmallText>
          <View>
            {tree.map(cat => {
              const isExpanded = expandedCategoryIds?.includes(cat.id);
              return (
                <View key={cat.id}>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <TouchableOpacity onPress={() => {
                      setExpandedCategoryIds(prev =>
                        prev?.includes(cat.id)
                          ? prev.filter(id => id !== cat.id)
                          : [...(prev || []), cat.id]
                      );
                    }}>
                      <Octicons
                        name={isExpanded ? 'chevron-down' : 'chevron-right'}
                        size={18}
                        color="#333"
                        style={{ marginRight: 6 }}
                      />
                    </TouchableOpacity>
                    <CheckBox
                      title={"Categoria: " + cat.title}
                      checked={cat.selected}
                      onPress={() => {
                        setTree(t =>
                          t.map(c =>
                            c.id === cat.id
                              ? {
                                ...c,
                                selected: !c.selected,
                                cards: c.cards.map(cd => ({
                                  ...cd,
                                  selected: !c.selected
                                }))
                              }
                              : c
                          )
                        );
                      }}
                      containerStyle={{ backgroundColor: 'transparent', paddingVertical: 0, flex: 1 }}
                    />
                  </View>

                  {isExpanded && cat.cards.map(cd => (
                    <CheckBox
                      key={cd.id}
                      title={"Módulo:" + cd.title}
                      checked={cd.selected}
                      onPress={() => {
                        setTree(t =>
                          t.map(c =>
                            c.id === cat.id
                              ? {
                                ...c,
                                cards: c.cards.map(cc =>
                                  cc.id === cd.id ? { ...cc, selected: !cc.selected } : cc
                                ),
                              }
                              : c
                          )
                        );
                      }}
                      containerStyle={{ backgroundColor: "transparent", paddingVertical: 0, paddingLeft: 24 }}
                      textStyle={{ fontSize: 14 }}
                    />
                  ))}
                </View>
              );
            })}
          </View>
        </View>
        <View style={styles.buttonContainer}>
          {!isIAP && (
            <Button title="Editar Ofertas" onPress={() => editOffers()}
              colorbutton={ADMIN_COLORS.secondaryBg} colortitle={ADMIN_COLORS.secondaryText} />
          )}
          {vitrineFeedback && (
            <View style={{ width: "100%", marginBottom: 8 }}>
              <AdminAlert variant={vitrineFeedback.variant}>
                {vitrineFeedback.message}
              </AdminAlert>
            </View>
          )}
          <Button
            title="Salvar alterações"
            isLoading={loadingSave}
            onPress={handleEdit}
            colorbutton={ADMIN_COLORS.primaryBg}
            colortitle={'#ffffff'}
          />
        </View>
        {onDelete && !isIAP && (
          <View style={{ marginHorizontal: 10, marginBottom: 10 }}>
            <Button
              title="Excluir produto"
              isLoading={isDeleting}
              onPress={() => onDelete(productData)}
              colorbutton={ADMIN_COLORS.dangerSoft}
              colortitle={ADMIN_COLORS.danger}
            />
            <SmallText textAlign="center" color={ADMIN_COLORS.muted} fontFamily={ADMIN_FONT}>
              Só é possível excluir um produto sem nenhum aluno com acesso ativo.
            </SmallText>
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  modalView: {
    backgroundColor: 'white',
    borderRadius: 10,
    overflow: 'hidden',
    maxWidth: 780,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingTop: 10,
    paddingBottom: 2,
    width: '100%',
  },
  closeX: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeXText: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.muted,
  },
  label: {
    fontWeight: 'bold',
    fontSize: 14,
    marginBottom: 4,
  },
  readOnlyText: {
    fontSize: 14,
    color: '#2e2e2e',
    marginBottom: 10,
  },
  closeButton: {
    position: 'absolute',
    top: 5,
    right: 5,
    zIndex: 1,
  },
  closeButtonText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#000',
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    margin: 10

  },
});

export default GenericProduct;
